import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Text, Paragraph } from '@/components';
import './Accordion.scss';

type AccordionItemProps = {
  id: string
  title: string;
  content: string | React.ReactNode;
  isOpen: boolean;
  onClick: () => void;
};

const AccordionItem = ({ id, title, content, isOpen, onClick }: AccordionItemProps) => (
  <div
    id={id}
    className={clsx(
    "flex flex-col items-start mb-3 accordion-header",
    {
      "active": isOpen
    }
  )}>
     <div className="w-full accordion-header-inner">
      <div
        className={clsx(
          "w-full p-5 flex justify-between",
          {
            "pb-[10px]": isOpen,
          }
        )}
        onClick={onClick}
      >
        <Text.Description
          size="sm"
          className={`justify-start text-left ${isOpen ? '!text-[#E3E3E3]' : '!text-[#B6B6B6]' }`}
        >
          {title}
        </Text.Description>

        {isOpen ? (
          <img
            src="/images/home/active-up.svg"
            alt="Toggle Icon"
            className="w-[10px] h-auto"
            loading="lazy" // Added lazy loading
          />
        ) : (
          <img
            src="/images/home/down.svg"
            alt="Toggle Icon"
            className="w-[10px] h-auto"
            loading="lazy" // Added lazy loading
          />
        )}
      </div>
      
      {/* Apply max-height for animation */}
      <div 
        className={clsx("accordion-content", { "open": isOpen })}
        style={{ maxHeight: isOpen ? '1200px' : '0' }} // Adjust 500px as needed
      >
        <div className="flex px-5 pb-5">
          {typeof content === 'string' ? (
            <Text.Description size="xs" className="text-left !text-[#E3E3E3]">
              {content}
            </Text.Description>
          ) : (
            <Paragraph>
              {content}
            </Paragraph>
          )}
        </div>
      </div>
     </div>
  </div>
);

type AccordionProps = {
  items: { id?: string; title: string; content: string | React.ReactNode }[];
  defaultActiveTab?: number
};

export const Accordion = ({ items, defaultActiveTab }: AccordionProps) => {
  const [openIndex, setOpenIndex] = useState<number | null>(defaultActiveTab ?? null);

  useEffect(() => {
    setOpenIndex(defaultActiveTab ?? null);
  }, [defaultActiveTab]);

  const handleToggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="w-full">
      {items.map((item, index) => (
        <AccordionItem
          key={index}
          id={item.id || `faq-${index}`}
          title={item.title}
          content={item.content}
          isOpen={openIndex === index}
          onClick={() => handleToggle(index)}
        />
      ))}
    </div>
  );
};
